import {
  Component,
  computed,
  DestroyRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  Output,
  EventEmitter,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { catchError, EMPTY, skip, switchMap, tap } from 'rxjs';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { GridComponent } from '../../../../../shared/components/grid/grid.component';
import { CustomerModel } from '../../../../customer/customer.model';
import { customersConfig } from '../../../../customer/config/customers';
import { normalizeLookupTerm } from '../../../../customer/utils/customer-lookup';
import { CustomerPageQuery, CustomersService, CustomerSortField } from '../../../../customer/services/customers.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { GridViewType } from '../../../../../shared/components/grid/grid-config';
import { debouncedSearch } from '../../../../../shared/utils/rx-operators';
import { PaymentCollectStore } from '../../../store/payment-collect.store';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-payment-collect-customers',
  templateUrl: './customers.component.html',
  providers: [ {
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => PaymentCollectCustomersComponent)
  }],
  standalone: true,
  imports: [
    GridComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule
  ],
  styles: [
    `
      /* Same lookups, and the same look, as the Customers page. The .search-input shell is the
         grid's own (global _grid.scss), so they match the Search box beside them. */
      .app-payment-collect-customers__lookups {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .app-payment-collect-customers__lookup {
        background-color: #fff;
        width: 170px;
      }
      .app-payment-collect-customers__lookup input[type='text'] {
        width: 100%;
      }
      .app-payment-collect-customers__lookup-clear {
        height: 33px;
        line-height: 33px;
        display: flex;
        align-items: center;
        white-space: nowrap;
      }
      .app-payment-collect-customers__lookup-clear mat-icon {
        font-size: 18px;
        height: 18px;
        width: 18px;
        line-height: 18px;
      }
    `,
  ],
})
export class PaymentCollectCustomersComponent implements ControlValueAccessor, OnInit {
  @Input() parent!: any;

  config = STEP_ONE_CONFIG;

  protected onChange: (value: CustomerModel) => void = () => {};
  protected onTouched: () => void = () => {};
  protected selected: CustomerModel = {};

  @HostListener('click', ['$event'])
  onClick(event: Event) {
    const target = event.target as HTMLElement;
    
    // Simplified list of elements to ignore
    if (target.tagName === 'BUTTON' || 
        target.tagName === 'MAT-ICON' ||
        target.tagName === 'svg' ||  // SVG elements are key to fix
        target.closest('button') || 
        target.closest('mat-icon') ||
        target.closest('svg')) {
      // Only prevent default behavior
      event.preventDefault();
      return;
    }
    
    this.onTouched();
  }
  readonly store = inject(PaymentCollectStore);
  private readonly customersService = inject(CustomersService);
  private readonly notificationService = inject(NotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Account number and customer name lookups, in place of the grid's generic "Filter by". FTE
   * matches each as a case-insensitive contains on its own field, the two combined with AND.
   */
  readonly accountNumberFilter = new FormControl('');
  readonly customerNameFilter = new FormControl('');

  /** The applied terms, which lag the boxes by the shared debounce. */
  private readonly accountNumberTerm = signal('');
  private readonly customerNameTerm = signal('');

  /**
   * The page the grid shows, fetched from `GET /customers` a page at a time. A distributor's book
   * runs to thousands of customers, and loading all of it to show ten took seconds.
   */
  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);
  /** Grid column and direction. Defaults to most recently modified, the order the book was shown in. */
  readonly sort = signal<Sort>({ active: '', direction: '' });

  readonly rows = signal<CustomerModel[]>([]);
  readonly total = signal(0);
  readonly loading = signal(true);

  /** Everything that decides which page to fetch. */
  private readonly query = computed<CustomerPageQuery>(() => {
    const { sortBy, sortOrder } = toServerSort(this.sort());
    return {
      page: this.pageIndex() + 1,
      limit: this.pageSize(),
      accountNumber: this.accountNumberTerm() || undefined,
      name: this.customerNameTerm() || undefined,
      sortBy,
      sortOrder,
      inactive: 'exclude',
      // A ship-to account is a delivery location the ERP bills through another customer: it can
      // never hold an invoice, and FTE refuses a payment against one with 409
      // `CUSTOMER_NOT_BILLABLE`. Left out rather than shown disabled, for the same reason
      // `multi-invoice-selection` drops a NotPayable invoice. It is still listed and badged on the
      // Customers page.
      billable: 'exclude',
      includeTokenSummary: true,
    };
  });

  /** True while either lookup is narrowing the grid — drives the Clear button. */
  readonly hasLookup = computed(() => !!this.accountNumberTerm() || !!this.customerNameTerm());

  constructor() {
    toObservable(this.query)
      .pipe(
        tap(() => this.loading.set(true)),
        // A newer query cancels one still in flight, so a slow page never overwrites a later one.
        switchMap((query) =>
          this.customersService.getCustomersPage(query).pipe(
            catchError((error: Error) => {
              this.notificationService.error(error?.message ?? 'Could not load customers');
              this.loading.set(false);
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe((page) => {
        this.rows.set(page.rows ?? []);
        this.total.set(page.count ?? 0);
        this.loading.set(false);
      });

    // A customer added or edited elsewhere in the collect (or from a row's menu): show it as it
    // is now. Skips the current value, which the first fetch already covers.
    toObservable(this.store.customersVersion)
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe(() => this.refresh());
  }

  /** Re-fetches the page in view. */
  refresh(): void {
    // A new object, so the query signal emits even though nothing in it changed.
    this.pageIndex.update((index) => index);
    this.sort.update((sort) => ({ ...sort }));
  }

  onPage(event: PageEvent): void {
    this.pageSize.set(event.pageSize);
    this.pageIndex.set(event.pageIndex);
  }

  onSort(sort: Sort): void {
    this.sort.set(sort);
    this.pageIndex.set(0);
  }

  clearLookups(): void {
    this.accountNumberFilter.setValue('');
    this.customerNameFilter.setValue('');
    // Applied now rather than after the debounce: a deliberate Clear is not typing.
    this.applyTerms('', '');
  }

  @Output() customerSelected = new EventEmitter<void>();

  writeValue(customer: CustomerModel): void {
    this.setSelected(customer);
  }

  registerOnChange(fn: (value: CustomerModel) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  selectCustomer(customer: CustomerModel) {
    this.setSelected(customer);
    // The store reloads the customer's accounts itself when the selected customer changes.
    this.onChange(this.selected);
    this.customerSelected.emit();
  }

  /**
   * The grid's row-highlight test. Replaced only when the selection changes: the grid is OnPush,
   * so a new function is what tells it to repaint.
   */
  isSelectedFunc: (customer: CustomerModel) => boolean = () => false;

  private setSelected(customer: CustomerModel): void {
    this.selected = customer;
    const selectedId = customer?.id;
    this.isSelectedFunc = (row: CustomerModel) => !!selectedId && row.id === selectedId;
  }

  /** Sets both lookup terms and returns to the first page, since the old page may not exist. */
  private applyTerms(accountNumber: string, customerName: string): void {
    this.accountNumberTerm.set(accountNumber);
    this.customerNameTerm.set(customerName);
    this.pageIndex.set(0);
  }

  ngOnInit() {
    this.accountNumberFilter.valueChanges
      .pipe(debouncedSearch(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.applyTerms(normalizeLookupTerm(value), this.customerNameTerm()));

    this.customerNameFilter.valueChanges
      .pipe(debouncedSearch(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.applyTerms(this.accountNumberTerm(), normalizeLookupTerm(value)));

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const customerId = params.get('customerId');
      // Already the form's customer (a collect started from an invoice, then "change"): selecting
      // it again would only reload its accounts.
      if (customerId && customerId !== this.selected?.id) {
        this.autoSelect(customerId);
      }
    });
  }

  /**
   * Selects the customer a link named (`?customerId=`), fetched on its own since it need not be
   * on the page in view.
   */
  private autoSelect(customerId: string): void {
    this.customersService
      .getCustomer(customerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (customer) => {
          // A deep link naming a ship-to must not select one either — the refusal would otherwise
          // only surface at the gateway step, after the operator had keyed a card.
          if (customer && customer.shipToOnly !== true) {
            this.selectCustomer(customer);
          }
        },
        error: (error) => console.error('Error loading linked customer:', error),
      });
  }
}

/**
 * Grid column → `GET /customers` sort field. The customer column shows the account number first,
 * so it sorts by that. Anything unmapped (or no sort) falls back to most recently modified.
 */
const SORT_FIELDS: Record<string, CustomerSortField> = {
  customerLabel: 'companyAccountNumber',
  companyAccountNumber: 'companyAccountNumber',
  contactLastName: 'contactLastName',
  contactFirstName: 'contactFirstName',
};

function toServerSort(sort: Sort): Pick<CustomerPageQuery, 'sortBy' | 'sortOrder'> {
  const field = sort.direction ? SORT_FIELDS[sort.active] : undefined;
  return field
    ? { sortBy: field, sortOrder: sort.direction as 'asc' | 'desc' }
    : { sortBy: 'lastModified', sortOrder: 'desc' };
}

/**
 * The Customers grid's columns, sortable only where FTE can order by them. Payment types come from
 * the customer's tokens, which the paged query does not sort on.
 */
const STEP_ONE_CONFIG: GridViewType<CustomerModel> = {
  ...customersConfig,
  columns: customersConfig.columns?.map((column) =>
    column.isActionColumn || SORT_FIELDS[column.code] ? column : { ...column, sortable: false }
  ),
};
